(() => {
  'use strict';

  const PROCESS_SELECTOR = '[data-sfh3-process]';
  const REVEAL_SELECTOR = '[data-sfh3-reveal]';
  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function revealWithin(scope = document) {
    const items = [...scope.querySelectorAll(REVEAL_SELECTOR)];
    if (!items.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-revealed'));
      return;
    }

    const roots = new Set(items.map((item) => item.closest('.sfh3-section')).filter(Boolean));
    roots.forEach((root) => root.classList.add('is-motion-ready'));

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    items.forEach((item) => observer.observe(item));
  }

  class HealthProcess {
    constructor(root) {
      this.root = root;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.tabs = [...root.querySelectorAll('[data-sfh3-step]')];
      this.panels = [...root.querySelectorAll('[data-sfh3-panel]')];
      this.previous = root.querySelector('[data-sfh3-previous]');
      this.next = root.querySelector('[data-sfh3-next]');
      this.toggle = root.querySelector('[data-sfh3-toggle]');
      this.toggleText = root.querySelector('[data-sfh3-toggle-text]');
      this.status = root.querySelector('[data-sfh3-status]');
      this.index = 0;
      this.interval = Math.max(4000, Number(root.dataset.interval) || 6500);
      this.userPaused = root.dataset.autoplay !== 'true';
      this.inViewport = false;
      this.pointerInside = false;
      this.focusInside = false;
      this.timer = null;

      if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

      this.bind();
      this.select(0, false);
      this.observe();
      this.syncPlayback();
    }

    bind() {
      const options = { signal: this.signal };

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true), options);
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index), options);
      });

      this.previous?.addEventListener('click', () => this.select(this.index - 1, true), options);
      this.next?.addEventListener('click', () => this.select(this.index + 1, true), options);
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.syncPlayback();
      }, options);

      this.root.addEventListener('pointerenter', () => {
        this.pointerInside = true;
        this.syncPlayback();
      }, options);
      this.root.addEventListener('pointerleave', () => {
        this.pointerInside = false;
        this.syncPlayback();
      }, options);
      this.root.addEventListener('focusin', () => {
        this.focusInside = true;
        this.syncPlayback();
      }, options);
      this.root.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.root.contains(event.relatedTarget)) return;
        this.focusInside = false;
        this.syncPlayback();
      }, options);

      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      reducedMotion.addEventListener?.('change', () => this.syncPlayback(), options);
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.inViewport = true;
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        this.inViewport = Boolean(entries[0]?.isIntersecting);
        this.syncPlayback();
      }, { threshold: 0.35 });
      this.visibilityObserver.observe(this.root);
    }

    normalize(index) {
      return (index + this.tabs.length) % this.tabs.length;
    }

    select(nextIndex, announce) {
      this.index = this.normalize(nextIndex);

      this.tabs.forEach((tab, index) => {
        const active = index === this.index;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      this.panels.forEach((panel, index) => {
        const active = index === this.index;
        panel.hidden = !active;
        panel.toggleAttribute('inert', !active);
        panel.setAttribute('aria-hidden', String(!active));
      });

      this.root.style.setProperty('--sfh3-step', String(this.index + 1));
      this.root.style.setProperty('--sfh3-count', String(this.tabs.length));

      if (announce && this.status) {
        const title = this.tabs[this.index].querySelector('strong')?.textContent?.trim() || `Schritt ${this.index + 1}`;
        this.status.textContent = `Schritt ${this.index + 1} von ${this.tabs.length}: ${title}`;
      }

      this.syncPlayback();
    }

    onTabKeydown(event, index) {
      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index + 1;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      this.select(nextIndex, true);
      this.tabs[this.index].focus();
    }

    syncPlayback() {
      window.clearTimeout(this.timer);
      this.timer = null;
      this.root.dataset.playState = 'paused';
    }

    destroy() {
      window.clearTimeout(this.timer);
      this.visibilityObserver?.disconnect();
      this.abortController.abort();
      instances.delete(this.root);
    }
  }

  function mount(scope = document) {
    revealWithin(scope);
    scope.querySelectorAll(PROCESS_SELECTOR).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new HealthProcess(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(PROCESS_SELECTOR);
    instances.get(root)?.destroy();
  });
})();
